import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import { ShieldCheck, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { API } from '../App';
import { useTheme } from '../context/ThemeContext';
import { useCredits } from '../context/CreditContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useSiteContent } from '../contexts/SiteContentContext';

// Single global AuthModal mounted once (in MainLayout) that can be opened
// from any page via `useAuthModal().openAuth(redirectPath?)`. Replaces the
// page-local modal that used to live inside Landing.jsx so guests never
// get bounced to the homepage to sign in.

const markAsReturningUser = () => {
  try { localStorage.setItem('ikizamini_returning_user', 'true'); } catch (_) {}
};

export default function AuthModal() {
  const { isOpen, openAuth, closeAuth } = useAuthModal();
  const { isDark } = useTheme();
  const { onLogin } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const { content: siteContent = {} } = useSiteContent();

  const [authMode, setAuthMode] = useState('select'); // select | phone | otp | google
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const textMuted = isDark ? 'text-gray-400' : 'text-gray-600';

  // Open via `?signin=1` in the URL (legacy entry point — kept so old
  // share links / fallbacks still work). Strip the param after handling.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('signin') === '1') {
      openAuth();
      sp.delete('signin');
      const newSearch = sp.toString();
      window.history.replaceState(
        {}, '',
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash
      );
    }
  }, [location.pathname, openAuth]);

  // Reset transient form state when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setAuthMode('select');
      setPhoneNumber('');
      setOtpCode('');
      setPhoneError('');
      setOtpCooldown(0);
      setIsLoading(false);
    }
  }, [isOpen]);

  const resetPhoneAuth = () => {
    setAuthMode('select');
    setPhoneNumber('');
    setOtpCode('');
    setPhoneError('');
    setOtpCooldown(0);
  };

  const handleClose = () => {
    closeAuth();
    resetPhoneAuth();
  };

  // Shared post-login redirect: prefer explicit pending redirect (set by
  // openAuth caller), then pending topic. When neither is set, check
  // whether the user has an active subscription — if not, bounce them
  // to /credits with the "benefits" flag so the subscription prompt
  // appears right after login.
  const finishLogin = async (userData) => {
    let pendingTopic = null;
    let pendingRedirect = null;
    try {
      pendingTopic = localStorage.getItem('ikizamini_pending_topic');
      pendingRedirect = localStorage.getItem('ikz_pending_redirect');
      localStorage.removeItem('ikizamini_pending_topic');
      localStorage.removeItem('ikz_pending_redirect');
    } catch (_) {}

    if (pendingRedirect) {
      navigate(pendingRedirect, { state: { user: userData } });
      return;
    }
    if (pendingTopic) {
      navigate(`/topic/${pendingTopic}`, { state: { user: userData } });
      return;
    }

    // No pending redirect — do the subscription check. We only route to
    // /credits when the user is NOT already there (so a login from the
    // credits page itself doesn't trigger a redundant navigate), and
    // never for admins (their accounts bypass the subscription gate).
    try {
      if (userData?.is_admin) return;
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true, timeout: 8000 });
      if (!res.data?.is_active) {
        try { localStorage.setItem('ikz_show_subscription_benefits', '1'); } catch (_) {}
        if (!window.location.pathname.startsWith('/credits')) {
          navigate('/credits', { state: { user: userData } });
        }
      }
    } catch (_) {
      // Network issue — stay on current route, the user can navigate to
      // /credits themselves. Not a blocker.
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    const maxRetries = 2;
    let lastError = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.post(
          `${API}/auth/google`,
          { credential: credentialResponse.credential },
          { withCredentials: true, timeout: 15000 }
        );
        const userData = response.data;
        closeAuth();
        try { await onLogin(); } catch (e) { /* ignore */ }
        markAsReturningUser();
        toast.success(`Welcome, ${userData.name}!`);
        finishLogin(userData);
        return;
      } catch (error) {
        lastError = error;
        if (error.response?.status === 400 || error.response?.status === 403) break;
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
    }
    toast.error(lastError?.response?.data?.detail || 'Sign in failed. Please try again.');
    setIsLoading(false);
  };

  const handleSendOtp = async () => {
    setPhoneError('');
    let normalized = phoneNumber.trim().replace(/\D/g, '');
    if (!normalized) { setPhoneError('Please enter your phone number'); return; }
    if (normalized.startsWith('0') && normalized.length === 10) normalized = '+250' + normalized.substring(1);
    else if (normalized.startsWith('250') && normalized.length === 12) normalized = '+' + normalized;
    else if (normalized.length === 9 && (normalized.startsWith('7') || normalized.startsWith('2'))) normalized = '+250' + normalized;
    else { setPhoneError('Please enter a valid Rwanda phone number (9 digits)'); return; }
    if (normalized.length !== 13) { setPhoneError('Please enter a valid 9-digit phone number'); return; }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/phone/send-otp`, { phone: normalized });
      if (response.data.status === 'cooldown') {
        setOtpCooldown(response.data.remaining_seconds);
        setPhoneError(`Please wait ${response.data.remaining_seconds} seconds`);
        const interval = setInterval(() => {
          setOtpCooldown(prev => {
            if (prev <= 1) { clearInterval(interval); setPhoneError(''); return 0; }
            return prev - 1;
          });
        }, 1000);
      } else if (response.data.status === 'sent') {
        setPhoneNumber(normalized);
        setAuthMode('otp');
        toast.success('Verification code sent!');
      }
    } catch (error) {
      setPhoneError(error.response?.data?.detail || 'Failed to send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) { setPhoneError('Please enter the 6-digit code'); return; }
    setIsLoading(true);
    setPhoneError('');
    try {
      const response = await axios.post(
        `${API}/auth/phone/verify-otp`,
        { phone: phoneNumber, code: otpCode },
        { withCredentials: true }
      );
      const userData = response.data;
      closeAuth();
      try { await onLogin(); } catch (e) { /* ignore */ }
      markAsReturningUser();
      toast.success(`Welcome, ${userData.name}!`);
      finishLogin(userData);
    } catch (error) {
      setPhoneError(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
          data-testid="auth-modal-overlay"
        >
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className={`w-full max-w-sm p-6 rounded-2xl ${isDark ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
            data-testid="auth-modal"
          >
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center mx-auto mb-3 shadow-lg">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-1">{siteContent.modal_welcome_title || 'Welcome'}</h3>
              <p className={`text-xs ${textMuted}`}>{siteContent.modal_welcome_subtitle || 'Sign in to save your progress'}</p>
            </div>

            {isLoading ? (
              <div className="p-4">
                <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className={`text-center text-xs ${textMuted} mt-2`}>
                  {authMode === 'otp' ? 'Verifying...' : authMode === 'google' ? 'Signing in...' : 'Sending code...'}
                </p>
              </div>
            ) : authMode === 'select' ? (
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setAuthMode('phone')}
                  data-testid="phone-login-btn"
                  className={`flex-1 flex flex-col items-center gap-2.5 py-5 px-4 rounded-2xl border-2 ${
                    isDark
                      ? 'border-brand-700 bg-brand-900/30 hover:bg-brand-900/50'
                      : 'border-brand-200 bg-brand-50/50 hover:bg-brand-100/70'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-brand-600' : 'bg-brand-500'}`}>
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-brand-300' : 'text-brand-700'}`}>{siteContent.modal_phone_btn || 'Phone'}</span>
                </button>
                <button
                  onClick={() => {
                    setAuthMode('google');
                    setTimeout(() => {
                      const gBtn = document.querySelector('[data-testid="auth-modal"] [data-google-mount] div[role="button"]');
                      if (gBtn) gBtn.click();
                    }, 100);
                  }}
                  data-testid="google-login-btn"
                  className={`flex-1 flex flex-col items-center gap-2.5 py-5 px-4 rounded-2xl border-2 ${
                    isDark
                      ? 'border-brand-700 bg-brand-900/30 hover:bg-brand-900/50'
                      : 'border-brand-200 bg-brand-50/50 hover:bg-brand-100/70'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-brand-300' : 'text-brand-700'}`}>Google</span>
                </button>
              </div>
            ) : authMode === 'google' ? (
              <div className="space-y-3">
                <div className="flex justify-center" data-google-mount>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={(error) => {
                      if (error !== 'popup_closed_by_user') toast.error('Google sign in failed. Please try again.');
                      setAuthMode('select');
                    }}
                    theme={isDark ? 'filled_black' : 'outline'}
                    size="large"
                    width="300"
                    text="continue_with"
                  />
                </div>
                <button onClick={() => setAuthMode('select')} className={`w-full text-xs ${textMuted} hover:underline`}>← Back</button>
              </div>
            ) : authMode === 'phone' ? (
              <div className="space-y-3">
                <div>
                  <label className={`block text-xs font-normal mb-1.5 ${textMuted}`}>Phone Number</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">🇷🇼</span>
                    <span className={`text-xs ${textMuted}`}>+250</span>
                    <input
                      type="tel"
                      value={phoneNumber.startsWith('+250') ? phoneNumber.substring(4) : phoneNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').substring(0, 9);
                        setPhoneNumber(val);
                        setPhoneError('');
                      }}
                      placeholder="78X XXX XXX"
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-200 bg-white'} focus:outline-none focus:ring-2 focus:ring-brand-500`}
                      data-testid="phone-input"
                    />
                  </div>
                  {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
                </div>
                <Button
                  onClick={handleSendOtp}
                  disabled={otpCooldown > 0}
                  size="sm"
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-normal"
                  data-testid="send-otp-btn"
                >
                  {otpCooldown > 0 ? `Wait ${otpCooldown}s` : 'Send Verification Code'}
                </Button>
                <button onClick={resetPhoneAuth} className={`w-full text-xs ${textMuted} hover:underline`}>← Back to login options</button>
              </div>
            ) : authMode === 'otp' ? (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <p className={`text-sm ${textMuted}`}>Enter the 6-digit code sent to</p>
                  <p className="font-medium">{phoneNumber}</p>
                </div>
                <div>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').substring(0, 6);
                      setOtpCode(val);
                      setPhoneError('');
                    }}
                    placeholder="000000"
                    maxLength={6}
                    className={`w-full text-center text-2xl tracking-[0.5em] px-4 py-3 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-200 bg-white'} focus:outline-none focus:ring-2 focus:ring-brand-500`}
                    data-testid="otp-input"
                    autoFocus
                  />
                  {phoneError && <p className="text-red-500 text-xs mt-1.5 text-center">{phoneError}</p>}
                </div>
                <Button
                  onClick={handleVerifyOtp}
                  disabled={otpCode.length !== 6}
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white"
                  data-testid="verify-otp-btn"
                >
                  Verify & Sign In
                </Button>
                <div className="flex justify-between text-sm">
                  <button onClick={() => setAuthMode('phone')} className={`${textMuted} hover:underline`}>← Change number</button>
                  <button
                    onClick={handleSendOtp}
                    disabled={otpCooldown > 0}
                    className={`text-brand-500 hover:underline ${otpCooldown > 0 ? 'opacity-50' : ''}`}
                  >
                    {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </div>
            ) : null}

            <p className={`text-center text-[10px] ${textMuted} mt-4`}>
              {siteContent.modal_terms_text || 'By signing in, you agree to our Terms & Privacy Policy'}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
